import { toaster } from "@/components/ui/toaster";
import { AppRoutes } from "@/constants/routes";
import {
  GetAccountListResponse,
  GetProductoresProps,
  getProductores,
} from "@/services/accounts.service";
import { getAxiosErrorMessage } from "@/utils/axios-error-message";
import { handlePermissionError } from "@/utils/permission-check";
import { Dispatch, SetStateAction } from "react";
import { FieldValues, UseFormSetValue } from "react-hook-form";
import { NavigateFunction } from "react-router-dom";
import {
  EstablishmentFRProps,
  getEstablishmentFilterRequest,
} from "./filter-obj";

interface FetchEstablishmentsHandlerProps {
  setLoading: Dispatch<SetStateAction<boolean>>;
  setEstablishments: Dispatch<SetStateAction<GetAccountListResponse | null>>;
  navigate: NavigateFunction;
  fetchPermissions: () => Promise<void>;
  showLoad?: boolean;
  filters?: Partial<GetProductoresProps>;
}

export const fetchEstablishmentsAndFieldsHandler = async ({
  setLoading,
  setEstablishments,
  navigate,
  fetchPermissions,
  showLoad = true,
  filters,
}: FetchEstablishmentsHandlerProps) => {
  try {
    if (showLoad) setLoading(true);

    const response = await getProductores({
      ...filters,
      "P.Pagina": (filters as any)?.["P.Pagina"] ?? 1,
      "P.Records": 10,
    });

    setEstablishments(response);
  } catch (error) {
    const handled = await handlePermissionError(
      error,
      navigate,
      fetchPermissions,
      AppRoutes.ESTABLISHMENTS_MAIN_PAGE,
    );

    if (!handled) {
      toaster.create({
        type: "error",
        title: getAxiosErrorMessage(
          error,
          "No se pudo cargar el listado de establecimientos.",
        ),
      });
    }
  } finally {
    if (showLoad) setLoading(false);
  }
};

interface SubmitFiltersHandlerProps {
  event: FieldValues;
  setOpenFilters: Dispatch<SetStateAction<boolean>>;
  setFiltersApplied: Dispatch<SetStateAction<boolean>>;
  setCurrentFilters: Dispatch<SetStateAction<Partial<GetProductoresProps>>>;
  setPage: Dispatch<SetStateAction<number>>;
  fetchEstablishments: (
    filters?: Partial<GetProductoresProps>,
    showLoad?: boolean,
  ) => Promise<void>;
}

export const submitFiltersHandler = async ({
  event,
  setOpenFilters,
  setFiltersApplied,
  setCurrentFilters,
  setPage,
  fetchEstablishments,
}: SubmitFiltersHandlerProps) => {
  const filterObj = getEstablishmentFilterRequest(event as EstablishmentFRProps);

  setCurrentFilters(filterObj);
  setFiltersApplied(Object.keys(filterObj).length > 0);
  setPage(1);

  await fetchEstablishments({ ...filterObj, "P.Pagina": 1 }, true);
  setOpenFilters(false);
};

interface ClearFiltersHandlerProps {
  setValue: UseFormSetValue<FieldValues>;
  setFiltersApplied: Dispatch<SetStateAction<boolean>>;
  setOpenFilters: Dispatch<SetStateAction<boolean>>;
  submitFilters: () => Promise<void>;
}

export const clearFiltersHandler = async ({
  setValue,
  setFiltersApplied,
  setOpenFilters,
  submitFilters,
}: ClearFiltersHandlerProps) => {
  setValue("account_number", null);
  setValue("account_type", "");
  setValue("locality", "");
  setValue("commercial_zone", "");
  setValue("social_reason", "");

  setFiltersApplied(false);
  setOpenFilters(false);
  await submitFilters();
};
